import {
  CustomerType,
  EnergyType,
  evDayLoadProfile,
  evNightLoadProfile,
  evNightSlugs,
  State,
  TooltipText,
} from '@energy-retailer/constants';
import { z } from 'zod';
import { getDistributorByID } from '../distributor-schema';
import { getProductByName } from '../product-schema';
import {
  evSlugs,
  mappedDistributorsFromBusinessForVicEVPlans,
} from '@energy-retailer/constants';

export const segmentToCustomerType = (value: string): CustomerType => {
  if (value === 'BUS') return CustomerType.Business;
  if (value === 'RES') return CustomerType.Residential;
  throw new Error(
    `Error transforming customer type; unknown Segment in CSV: ${value}`
  );
};

export enum SegmentEnum {
  BUS = 'BUS',
  RES = 'RES',
}

export const normalisedBPIDSchema = z.object({
  energyType: z.nativeEnum(EnergyType),
  distributor: z.string(),
  customerType: z.nativeEnum(CustomerType),
  tariffType: z.string(),
  planName: z.string(),
  planID: z.string(),
  climateZone: z.string(),
  url: z.string(),
  publishAt: z.number().optional(),
});

export type NormalisedBPID = z.infer<typeof normalisedBPIDSchema>;

export const bpidSchema = normalisedBPIDSchema.transform((bpid) => {
  return {
    ...bpid,
    distributor: getDistributorByID.parse(bpid.distributor),
  };
});

export type BPID = z.infer<typeof bpidSchema>;

export const plansParametersSchema = z.object({
  controlledLoad: z.boolean().default(false),
  customerType: z.nativeEnum(CustomerType).default(CustomerType.Residential),
  distributor: z.string(),
  energyType: z.nativeEnum(EnergyType).default(EnergyType.Electricity),
  postcode: z.string().default('3000'),
  ntc: z.string().transform((value) => {
    // handle edge-case NTC transforms
    const [ntc, ...clNTCs] = value.split(':').filter((value) => value !== '');

    if (ntc === 'D1' && clNTCs.includes('DD1')) {
      return ['D1', 'D2OP'].join(':');
    }

    return value;
  }),
  promotionId: z.coerce.string().default('0'),
});

export const defaultPlansParametersSchema = z.object({
  controlledLoad: z.boolean().default(false),
  customerType: z.nativeEnum(CustomerType).default(CustomerType.Residential),
  energyType: z.nativeEnum(EnergyType).default(EnergyType.Electricity),
  postcode: z.string(),
  promotionId: z.coerce.string().default('0'),
});

const sharedFilterConditionsSchema = z.object({
  distributor: z.string(),
  energyType: z.enum([EnergyType.Electricity, EnergyType.Gas]),
  product: z.string().optional(),
});

const electricityPlanFilterConditionsSchema = z.object({
  energyType: z.literal(EnergyType.Electricity),
  controlledLoad: z.boolean(),
  ntcs: z.array(z.string()).optional(),
  clntcs: z.array(z.string()).optional(),
});

const gasPlanFilterConditionsSchema = z.object({
  energyType: z.literal(EnergyType.Gas),
  customerType: z.nativeEnum(CustomerType).default(CustomerType.Residential),
});

export const filterConditionsSchema = sharedFilterConditionsSchema.and(
  z.discriminatedUnion('energyType', [
    electricityPlanFilterConditionsSchema,
    gasPlanFilterConditionsSchema,
  ])
);

export type GetPlansParameters = z.input<typeof plansParametersSchema>;
export type GetDefaultPlansParameters = z.input<
  typeof defaultPlansParametersSchema
>;

export const planParametersSchema = z
  .object({
    planID: z.string().optional(),
    docOnly: z.string().default('false'),
  })
  .transform((val) => {
    return {
      planID: val.planID,
      docOnly: val.docOnly === 'true',
    };
  })
  .refine((val) => {
    // if we are only looking for docs, we don't need a planID
    if (!val.docOnly) {
      // ensure we only search for a single planID
      if (!val.planID) {
        return false;
      }

      return val.planID.length > 0 && val.planID.split(',').length === 1;
    }

    return true;
  });

export type GetPlanParameters = z.input<typeof planParametersSchema>;

export const planSlugParametersSchema = plansParametersSchema.and(
  z.object({
    slug: z.string(),
  })
);

export type GetPlanSlugParameters = z.input<typeof planSlugParametersSchema>;

export const estimatesSchema = z.object({
  annual: z.string(),
  quarterly: z.string(),
  monthly: z.string(),
  weekly: z.string(),
});

export const transformPrice = z.coerce.string().transform((value) => {
  return parseFloat(value).toFixed(2);
});

export const transformEstimates = z.preprocess(
  (value) => ({
    annual: transformPrice.parse(value),
    quarterly: transformPrice.parse(parseFloat(String(value)) / 4),
    monthly: transformPrice.parse(parseFloat(String(value)) / 12),
    weekly: transformPrice.parse(parseFloat(String(value)) / 52),
  }),
  estimatesSchema
);

export type EstimatesSchema = z.infer<typeof estimatesSchema>;

export const ratesSchema = z
  .object({
    supply: z.string(),
    highSeason: z.string(),
    lowSeason: z.string(),
    peak: z.string(),
    singleRate: z.string(),
    offPeak: z.string(),
    shoulder: z.string(),
    superOffPeak: z.string(),
    summerDemand: z.string(),
    nonSummerDemand: z.string(),
    peakDemand: z.string(),
    controlledLoadSupply: z.string(),
    controlledLoad: z.string(),
    controlledLoadSupply1: z.string(),
    controlledLoad1: z.string(),
    controlledLoadSupply2: z.string(),
    controlledLoad2: z.string(),
    default: z.string(),
    premium: z.string(),
    controlledLoadPeak: z.string(),
    controlledLoadShoulder: z.string(),
    controlledLoadOffPeak: z.string(),
  })
  .partial();

export const transformRates = ratesSchema
  .transform((value) => {
    const { peak, offPeak, singleRate, ...rates } = value;

    // if we have no offPeak, set singleRate tariff
    if (!Number(offPeak) && !Number(singleRate)) {
      return {
        ...rates,
        singleRate: peak,
      };
    }

    return value;
  })
  .transform((value) => {
    // remove unused keys
    return Object.entries<string>(value).reduce<
      Record<string, string | undefined>
    >(
      (
        acc: Record<string, string | undefined>,
        [key, val]: [string, string | undefined]
      ) => {
        if (Number.isNaN(Number(val)) || val === '') {
          return acc;
        }

        if (val !== undefined) {
          acc[key] = val;
        }

        return acc;
      },
      {}
    );
  });

export type RatesSchema = z.infer<typeof ratesSchema>;

const feedInRatesSchema = z.object({
  default: z.string(),
  premium: z.string(),
});

export type FeedInRatesSchema = z.infer<typeof feedInRatesSchema>;

const electricityPlanSchema = z.object({
  controlledLoad: z.boolean().default(false),
  cl: z.number().optional(),
  tariffType: z.string(),
  comparison: z.string().transform((value) => {
    return parseFloat(value).toFixed(2);
  }),
  clntcs: z.array(z.string()).default([]).optional(),
  energyType: z.literal(EnergyType.Electricity),
  feedInRates: feedInRatesSchema.partial().nullable(),
});

const gasPlanSchema = z.object({
  energyType: z.literal(EnergyType.Gas),
  zone: z.string(),
});

const offerToolTipSchema = z.object({
  id: z.string(),
  label: z.enum(['VDO', 'reference price']),
  text: z.enum([TooltipText.VDO, TooltipText.DMO]),
});

const sharedPlanSchema = z.object({
  planID: z.string().default(''),
  energyType: z.enum([EnergyType.Electricity, EnergyType.Gas]),
  key: z.string(),
  product: z.string(),
  state: z.nativeEnum(State),
  distributor: z.string(),
  customerType: z.nativeEnum(CustomerType),
  rates: ratesSchema,
  optionalRates: z.object({}).default({}),
  estimates: estimatesSchema,
  annualConsumption: z.number(),
  ntc: z.string(),
  ntcs: z.array(z.string()),
  docs: normalisedBPIDSchema.array().optional(),
  promotionId: z.string().optional(),
  hidePricing: z.boolean().default(true),
  offerTooltip: offerToolTipSchema,
  isVictorianEVPlanWithMappedDistributor: z.boolean().default(false),
  isEVPlan: z.boolean().default(false),
  evLoadProfile: z
    .strictObject({
      superOffPeak: z.string(),
      offPeak: z.string(),
      peak: z.string(),
    })
    .optional(),
});

export const normalisedPlanSchema = sharedPlanSchema.and(
  z.discriminatedUnion('energyType', [electricityPlanSchema, gasPlanSchema])
);

export type NormalisedPlanSchema = z.infer<typeof normalisedPlanSchema>;

export const planSchema = normalisedPlanSchema
  .transform((plan) => {
    return {
      ...plan,
      distributor: getDistributorByID.parse(plan.distributor),
      product: getProductByName.parse({
        name: plan.product,
        energyType: plan.energyType,
      }),
      docs: bpidSchema.array().parse(plan.docs),
    };
  })
  .transform((plan) => {
    if (plan.product.slug && evSlugs.includes(plan.product.slug)) {
      plan.isEVPlan = true;
    }

    return plan;
  })
  .transform((plan) => {
    // Of note below; for EV plans that are in VIC a new copy was required
    // to display percentiles instread of the default electricity copy.
    const isVictorianEvPlan = plan.isEVPlan && plan.state === State.VIC;

    const distributorIsMapped =
      mappedDistributorsFromBusinessForVicEVPlans.includes(
        plan.distributor.name
      );

    plan.isVictorianEVPlanWithMappedDistributor =
      isVictorianEvPlan && distributorIsMapped;

    return plan;
  })
  .transform((plan) => {
    if (!plan.isVictorianEVPlanWithMappedDistributor) {
      return plan;
    }

    plan.evLoadProfile = evDayLoadProfile;

    if (evNightSlugs.includes(plan.product.slug)) {
      plan.evLoadProfile = evNightLoadProfile;
    }

    return plan;
  });

export type PlanSchema = z.infer<typeof planSchema>;
